import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import clsx from 'clsx';
import { useState } from 'react';
import FaqJsonSchema from 'components/Shared/FaqJsonSchema';
import Image from 'components/ui/ContentBlocks/Image';
import RichText from 'components/ui/ContentBlocks/RichText';
import Youtube from 'components/ui/ContentBlocks/Youtube';
import { sanitizeContentfulBlockId } from 'utils/contentful';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import Icon from '../Icon';
import styles from './Accordion.module.scss';
import type { Entry } from 'contentful';
import type {
  IAccordionBlockFields,
  IAccordionItem,
  IAccordionItemFields,
  IBlockImageFields,
  IBlockYoutubeFields,
} from 'types/contentful/generated';
import type { Faq } from 'types/Utilities';
import type { ReactNode } from 'react';

type Props = Partial<IAccordionItemFields> &
  Partial<IAccordionItem> & {
    blockId?: string;
    noRichText?: string;
    transparent?: boolean;
    children?: ReactNode | ReactNode[];
  };

const Item = ({
  blockId,
  title,
  text,
  media,
  noRichText,
  transparent,
  children,
  sys,
}: Props): JSX.Element | null => {
  const [showItem, setShowItem] = useState(false);
  const inspectorPropsForItem = useContentfulInspectorMode({
    entryId: sys?.id,
  });

  return (
    <div
      className={clsx(styles.item, transparent && styles.transparent)}
      id={sanitizeContentfulBlockId(blockId)}
    >
      <div
        className={styles.titleWrapper}
        onClick={() => {
          setShowItem(!showItem);
        }}
      >
        <h3
          className={styles.title}
          {...inspectorPropsForItem?.({ fieldId: 'title' })}
        >
          {title}
          <Icon
            className={clsx(styles.arrrow, showItem && styles.arrowOpen)}
            type="Arrow"
            color="#155263"
          />
        </h3>
      </div>
      {showItem && (
        <>
          <div
            className={clsx(styles.content, transparent && styles.transparent)}
          >
            {children}
            {
              // eslint-disable-next-line
              // @ts-ignore
              noRichText || (
                <RichText
                  fields={text}
                  inspectorProps={{
                    ...inspectorPropsForItem?.({ fieldId: 'text' }),
                  }}
                />
              )
            }
          </div>
          {media ? (
            <div className={styles.media}>
              {media.map((m, i) => {
                if (m.sys.contentType.sys.id === 'blockImage') {
                  return <Image key={i} {...(m.fields as IBlockImageFields)} />;
                }

                if (m.sys.contentType.sys.id === 'blockYoutube') {
                  return (
                    <Youtube key={i} {...(m.fields as IBlockYoutubeFields)} />
                  );
                }

                return null;
              })}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

type AccordionProps = {
  accordionItems?: IAccordionBlockFields['accordionItems'];
  isFaq?: boolean;
  transparent?: boolean;
  children?: ReactNode | ReactNode[];
  childrenLast?: boolean;
};

const Accordion = ({
  accordionItems,
  isFaq,
  transparent,
  children,
  childrenLast,
}: AccordionProps): JSX.Element => {
  const items: Entry<IAccordionItemFields & { noRichText?: string }>[] =
    // eslint-disable-next-line
    // @ts-ignore
    accordionItems?.items || accordionItems || null;

  const faqItems: Faq[] = [];
  if (isFaq) {
    for (const item of items) {
      const itemFields = item?.fields || item;
      if (itemFields.disableLocale) {
        continue;
      }

      faqItems.push({
        question: itemFields.title,
        answer:
          itemFields.noRichText ||
          // eslint-disable-next-line
          // @ts-ignore
          documentToHtmlString(itemFields.text.json || itemFields.text),
      });
    }
  }

  return (
    <>
      {isFaq && <FaqJsonSchema items={faqItems} />}
      <div className={styles.container}>
        <div className={clsx(childrenLast && styles.childrenLast)}>
          {children}
        </div>
        <div>
          {items?.map((item, i) => {
            const itemFields = item.fields || item;

            if (itemFields.disableLocale) {
              return null;
            }

            return <Item key={i} transparent={transparent} {...itemFields} />;
          })}
        </div>
      </div>
    </>
  );
};

Accordion.Item = Item;
export default Accordion;
